import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { isUsableUrlTitle, urlDisplayTitle, type UrlEntity } from '../domain/note'
import type { LinkAnswer } from '../domain/noteTypes'
import type { GistUiStatus, LinkAskUi } from '../links/gistController'
import { GIST_SETTINGS } from '../links/gistPolicy'
import { hasSpecificLinkImage, linkPreviewImageUrl } from '../links/linkMetadata'
import { linkPreviewPresentation } from '../links/linkPreviewCategory'
import { askFailureMessage, blockedMessage, linkSummary, linkSummaryNote, siteDescriptionOf } from '../links/linkSummary'
import { CopyIconButton } from './CopyIconButton'
import { Icon } from './Icon'
import { useLeaveSheet } from './sheetMotion'

/** The latest question and its answer, as a compact block in the card: the question, the first lines of the answer, Copy and Open full screen. */
export function AnswerLine({ answer, onExpand }: { answer: LinkAnswer; onExpand?: () => void }) {
  return <div className="gist-line answer-line">
    <p className="answer-line__question">{answer.prompt}</p>
    <div className="gist-line__row"><Icon name="spark" /><p className="answer-line__text">{answer.text}</p></div>
    <div className="gist-line__tools">
      <CopyIconButton text={answer.text} label="Copy answer" />
      {onExpand ? <button type="button" className="btn btn-ghost btn-square" aria-label="Open full screen" title="Open full screen" onClick={onExpand}><Icon name="expand" /></button> : null}
    </div>
  </div>
}

export interface LinkAnswersOverlayProps {
  url: UrlEntity
  /** `summary`, an answer's id, or `ask` (open on the question field). */
  initial: string
  /** The summary's read: its state, and a way to start or repeat it. */
  summaryStatus: GistUiStatus | null
  summaryDetail?: string | null
  onSummarise: () => void
  ask: LinkAskUi
  onClose: () => void
}

const PENDING = 'pending'

/**
 * Everything generated about a page, full screen: the link (picture, title, source, and an icon that visits it, always in view),
 * then the summary and each question's answer as a list in which only the selected one is open, and a field to ask the page
 * something new. Copy sits beside every text. Only the link's address and the person's own question ever leave the device.
 */
export function LinkAnswersOverlay({ url, initial, summaryStatus, summaryDetail, onSummarise, ask, onClose }: LinkAnswersOverlayProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const fieldRef = useRef<HTMLTextAreaElement>(null)
  const titleId = useId()
  const leave = useLeaveSheet(dialogRef, onClose)
  const answers = url.answers ?? []
  const [selected, setSelected] = useState(initial === 'ask' || initial === 'summary' ? 'summary' : initial)
  const [question, setQuestion] = useState('')
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null)
  const [failedImage, setFailedImage] = useState(false)
  const presentation = linkPreviewPresentation(url)
  const title = isUsableUrlTitle(url, url.title) ? urlDisplayTitle(url) : url.originalUrl
  const image = hasSpecificLinkImage(url, url.imageUrl) ? linkPreviewImageUrl(url.imageUrl) : null
  const meta = [presentation.provider === title ? null : presentation.provider, url.author].filter(Boolean).join(' · ')
  const summary = linkSummary(url, summaryStatus, summaryDetail)
  const [submitting, setSubmitting] = useState(false)
  const asking = submitting || ask.status === 'reading'
  const max = GIST_SETTINGS.ask.maxPromptChars

  useLayoutEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal()
    else dialog.setAttribute('open', '')
    return () => {
      if (!dialog.open) return
      if (typeof dialog.close === 'function') dialog.close()
      else dialog.removeAttribute('open')
    }
  }, [])
  // Opened to ask: land on the field, and on the newest answer so the list is already in view behind it.
  useEffect(() => {
    if (initial !== 'ask') return
    setSelected(answers[0]?.id ?? 'summary')
    const timer = window.setTimeout(() => fieldRef.current?.focus({ preventScroll: true }), 60)
    return () => window.clearTimeout(timer)
    // Only on opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // A new answer arrives selected.
  const newest = answers[0]?.id
  const seen = useRef(newest)
  useEffect(() => {
    if (newest && newest !== seen.current) { setSelected(newest); setQuestion(''); setPendingQuestion(null) }
    seen.current = newest
  }, [newest])
  // Opening the view is the moment to find out whether this page can be read at all, so a person is told before they write a question.
  useEffect(() => { if (ask.readability.state === 'unknown') ask.check() }, [])
  // A failure is cleared when the view closes (the ask object is new on every render, so it is read through a ref).
  const askRef = useRef(ask)
  askRef.current = ask
  useEffect(() => () => askRef.current.clearStatus(), [])

  const submit = async (event?: FormEvent) => {
    event?.preventDefault()
    const text = question.replace(/\s+/g, ' ').trim()
    if (!text || asking) return
    const before = selected
    setPendingQuestion(text)
    setSelected(PENDING)
    setSubmitting(true)
    const ok = await ask.ask(text).finally(() => setSubmitting(false))
    if (!ok) { setSelected(before); setPendingQuestion(null) }
  }
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) { event.preventDefault(); void submit() }
  }
  const blocked = ask.readability.state === 'blocked' ? blockedMessage(ask.readability.reason) : null
  const checking = ask.readability.state === 'checking'
  const site = siteDescriptionOf(url)
  const failure = ask.status && ask.status !== 'reading' && ask.status !== 'no-gist' ? askFailureMessage(ask.status, ask.statusDetail) : null
  const open = (id: string) => setSelected((current) => current === id ? '' : id)
  const chosen = asking && pendingQuestion ? PENDING : selected

  return <dialog ref={dialogRef} className="modal studio-sheet link-answers-modal" aria-labelledby={titleId}
    onCancel={(event) => { event.preventDefault(); leave() }}
    onKeyDownCapture={(event) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); leave() } }}>
    <div className="modal-box sheet-panel link-answers" tabIndex={-1} autoFocus>
      <header className="link-answers__head">
        {image && !failedImage
          ? <img className="link-answers__art" src={image} alt="" crossOrigin="anonymous" referrerPolicy="no-referrer" onError={() => setFailedImage(true)} />
          : <span className="link-answers__art link-answers__art--fallback"><Icon name={presentation.icon} /></span>}
        <div className="link-answers__id">
          <h2 id={titleId}>{title}</h2>
          {meta ? <p>{meta}</p> : null}
        </div>
        <a className="btn btn-ghost btn-square link-answers__visit" href={url.originalUrl} target="_blank" rel="noreferrer noopener"
          aria-label={`${presentation.actionLabel}: ${title}`} title={presentation.actionLabel}><Icon name="external" /></a>
        <button type="button" className="btn btn-ghost btn-square sheet-close" aria-label="Close" onClick={leave}><Icon name="close" /></button>
      </header>

      <div className="sheet-body link-answers__body">
        <ul className="link-answers__list">
          {asking && pendingQuestion ? <li className="link-answers__item" data-open="true">
            <div className="link-answers__label link-answers__label--static"><Icon name="spark" /><span>{pendingQuestion}</span></div>
            <div className="link-answers__content" role="status" aria-label="Reading the page">
              <span className="gist-line__bars" aria-hidden="true"><i /><i /><i /></span>
            </div>
          </li> : null}

          <li className="link-answers__item" data-open={chosen === 'summary' || undefined}>
            <button type="button" className="link-answers__label" aria-expanded={chosen === 'summary'} onClick={() => open('summary')}>
              <Icon name="spark" /><span>Summary</span><Icon name="chevron" />
            </button>
            {chosen === 'summary' ? <div className="link-answers__content">
              {summary.text && summary.source === 'gist' ? <>
                <p className="link-answers__text">{summary.text}</p>
                <div className="link-answers__tools">
                  <CopyIconButton text={summary.text} label="Copy summary" />
                  <button type="button" className="btn btn-ghost btn-square" aria-label="Summarize again" title="Summarize again" disabled={summaryStatus === 'reading'} onClick={onSummarise}><Icon name="repeat" /></button>
                </div>
              </> : summaryStatus === 'reading'
                ? <span className="gist-line__bars" role="status" aria-label="Reading the page"><i /><i /></span>
                : blocked
                  ? <p className="link-answers__text link-answers__text--muted" role="status">{blocked}</p>
                  : <>
                    <p className="link-answers__text link-answers__text--muted">Not written yet.</p>
                    <button type="button" className="btn" onClick={onSummarise}>Summarize page</button>
                  </>}
              {!blocked && linkSummaryNote(summary) && summaryStatus !== 'reading' ? <p className="gist-line__note" role="status">{linkSummaryNote(summary)}</p> : null}
            </div> : null}
          </li>

          {site ? <li className="link-answers__item" data-open={chosen === 'site' || undefined}>
            <button type="button" className="link-answers__label" aria-expanded={chosen === 'site'} onClick={() => open('site')}>
              <Icon name="document" /><span>From the site</span><Icon name="chevron" />
            </button>
            {chosen === 'site' ? <div className="link-answers__content">
              <p className="link-answers__text">{site}</p>
              <p className="gist-line__note">Written by the site, not by miniMany.</p>
            </div> : null}
          </li> : null}

          {answers.map((answer) => <li className="link-answers__item" key={answer.id} data-open={chosen === answer.id || undefined}>
            <button type="button" className="link-answers__label" aria-expanded={chosen === answer.id} onClick={() => open(answer.id)}>
              <Icon name="help" /><span>{answer.prompt}</span><Icon name="chevron" />
            </button>
            {chosen === answer.id ? <div className="link-answers__content">
              <p className="link-answers__text">{answer.text}</p>
              <div className="link-answers__tools">
                <CopyIconButton text={answer.text} label="Copy answer" />
                <button type="button" className="btn btn-ghost btn-square" aria-label="Delete this answer" title="Delete this answer" onClick={() => { setSelected('summary'); ask.remove(answer.id) }}><Icon name="trash" /></button>
              </div>
            </div> : null}
          </li>)}
        </ul>
      </div>

      {blocked && !answers.length ? <div className="link-answers__ask link-answers__ask--blocked" role="status">
        <Icon name="help" /><p>{blocked}</p>
      </div> : <form className="link-answers__ask" onSubmit={submit}>
        <label className="sr-only" htmlFor={`${titleId}-ask`}>Ask this page</label>
        <textarea id={`${titleId}-ask`} ref={fieldRef} className="textarea link-answers__field" rows={2} maxLength={max} value={question} placeholder="Ask this page…"
          disabled={asking || Boolean(blocked)} onChange={(event) => setQuestion(event.target.value)} onKeyDown={handleKeyDown} />
        <button type="submit" className="btn btn-primary btn-square link-answers__send" aria-label="Ask" title="Ask" disabled={asking || Boolean(blocked) || !question.trim()}><Icon name="arrow-up" /></button>
        {blocked ? <p className="gist-line__note link-answers__failure" role="status">{blocked}</p>
          : failure ? <p className="gist-line__note link-answers__failure" role="status">{failure}</p>
          : checking ? <p className="gist-line__note link-answers__failure" role="status">Checking that this page can be read…</p> : null}
      </form>}
    </div>
  </dialog>
}
